import { Callout } from "@/components/ui/Callout";
import { BodyText } from "@/components/ui/Text";
import {
  callPanelOpenAtom,
  confirmationOpenAtom,
} from "@/features/AudioVideo/atoms/videoCallAtoms";
import { DailyCallContainer } from "@/features/AudioVideo/components/DailyCallContainer";
import { useJoinCall } from "@/features/AudioVideo/hooks/useJoinCall";
import { useRoomToken } from "@/features/Patient/network/useRoomToken";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useParams, useSearch } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useCallback } from "react";
import { ChatFilterTabs } from "../components/ChatFilterTabs";
import { ChatLoadingState } from "../components/ChatLoadingState";
import { ChatModeBanner } from "../components/ChatModeBanner";
import { JumpToBottomButton } from "../components/JumpToBottomButton";
import { useJumpButton } from "../hooks/useJumpButton";
import { ChatFilterMode } from "../types/chatMessageTypes";
import { UniversalChatInput } from "./components/UniversalChatInput";
import { UniversalChatMessage } from "./components/UniversalChatMessage";
import { useUniversalChatInfiniteScroll } from "./hooks/useUniversalChatInfiniteScroll";
import { useUniversalChatScroll } from "./hooks/useUniversalChatScroll";
import { useUniversalChatSocketRoom } from "./hooks/useUniversalChatSocket";
import { useUniversalClinicDailyEventHandling } from "./hooks/useUniversalClinicDailyEventHandling";
import { useFetchMessages } from "./network/useFetchMessages";
import type {
  OutgoingChatMessage,
  TextWebSocketMessageData,
} from "./types/universalChatTypes";
import { groupMessagesBySender } from "./utils/messageGrouping";

// Disabled filters for UC - only Live is available
const DISABLED_FILTERS = [ChatFilterMode.ASYNC, ChatFilterMode.COMBINED];

export const UniversalClinicChat = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: chatFilterEnabled } = useConfigCatFeatureFlag("decouple-chat");
  const callPanelOpen = useAtomValue(callPanelOpenAtom);
  const confirmationOpen = useAtomValue(confirmationOpenAtom);

  const { data: roomToken, error: roomTokenError } = useRoomToken({
    patientId,
    encounterId: encounterId?.toString(),
  });

  const {
    toggleVideoVisitCall,
    isCallPending,
    leaveCall,
    callObject,
    addTranslator,
    removeTranslator,
  } = useJoinCall();

  const {
    data,
    error,
    status,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useFetchMessages(roomToken as string);

  const { sendMessage: sendSocketMessage } = useUniversalChatSocketRoom(
    roomToken || "",
  );

  const sendMessage = useCallback(
    (message: OutgoingChatMessage) => {
      // Force the outbound payload into the text socket shape; our form hands
      // us an `OutgoingChatMessage` with a loose `type`, but the socket layer
      // expects the literal "text" discriminant for routing/validation.
      const socketMessage: TextWebSocketMessageData = {
        ...message,
        type: message.type as "text",
      };
      sendSocketMessage(socketMessage);
    },
    [sendSocketMessage],
  );

  const { scrollContainerRef, scrollToNewest } = useUniversalChatScroll();
  const { loadMoreRef } = useUniversalChatInfiniteScroll({
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
  });

  // `bottomRef` marks the newest edge; the button shows when it scrolls away.
  const { showJumpButton, bottomRef } = useJumpButton();

  const isMissingRoomToken = roomTokenError?.response?.status === 404;

  const renderContent = () => {
    if (isMissingRoomToken) {
      return (
        <div className="flex items-center justify-center flex-1 p-4">
          <Callout
            title="No chat yet"
            description="This patient hasn't started a chat in the new Curai Health app yet, so there's no conversation to show. Chat messages from their legacy account aren't available here. Messages will appear once the patient starts a visit."
            variant="info"
            className="max-w-md"
          />
        </div>
      );
    }

    if (status === "pending") {
      return <ChatLoadingState />;
    }

    if (status === "error") {
      return (
        <div className="flex items-center justify-center flex-1 p-4">
          <Callout
            title="Something went wrong"
            description={`Unable to load messages: ${error.message}`}
            variant="danger"
            className="max-w-md"
          />
        </div>
      );
    }

    return (
      <div className="flex-1 flex flex-col overflow-hidden h-full">
        <div
          ref={scrollContainerRef}
          className="flex flex-col-reverse flex-1 overflow-y-auto px-4 py-2 gap-2"
        >
          {/* First child = newest edge in the flipped container */}
          <div ref={bottomRef} className="h-1" />

          {data?.messages?.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <BodyText size="sm" textColor="tertiary">
                No messages yet
              </BodyText>
            </div>
          ) : (
            groupMessagesBySender(data?.messages || []).map(
              (groupedMessage) => (
                <UniversalChatMessage
                  key={
                    groupedMessage.message.id || groupedMessage.message.clientId
                  }
                  message={groupedMessage.message}
                  includeAvatar={
                    groupedMessage.isLastInGroup &&
                    groupedMessage.senderType !== "system"
                  }
                  participants={data?.participants}
                />
              ),
            )
          )}

          {/* Last child = oldest edge; scrolling up here loads older pages */}
          {hasNextPage && (
            <div
              ref={loadMoreRef}
              className="h-8 flex items-center justify-center"
            >
              {isFetchingNextPage && (
                <BodyText size="sm" textColor="tertiary">
                  Loading more messages...
                </BodyText>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative h-full flex flex-col">
      {(callPanelOpen || confirmationOpen) && (
        <DailyCallContainer
          callObject={callObject}
          leaveCall={leaveCall}
          addTranslator={addTranslator}
          removeTranslator={removeTranslator}
          useEventHandling={useUniversalClinicDailyEventHandling}
        />
      )}

      {/* Filter tabs - UC only has Live chat, so Async/Combined are disabled */}
      {chatFilterEnabled && (
        <ChatFilterTabs
          selectedFilter={ChatFilterMode.LIVE}
          onFilterChange={() => {}}
          disabledFilters={DISABLED_FILTERS}
        />
      )}

      {/* Mode banner - shows what user is viewing */}
      {chatFilterEnabled && <ChatModeBanner filterMode={ChatFilterMode.LIVE} />}

      {renderContent()}

      <JumpToBottomButton
        onClick={scrollToNewest}
        show={
          status === "success" &&
          showJumpButton &&
          (data?.messages?.length ?? 0) > 0
        }
      />

      {encounterId && !isMissingRoomToken && (
        <UniversalChatInput
          onSendMessage={sendMessage}
          disabled={status === "pending" || status === "error"}
          roomId={roomToken || ""}
          toggleVideoVisitCall={() =>
            toggleVideoVisitCall(encounterId?.toString())
          }
          isCallPending={isCallPending}
        />
      )}
    </div>
  );
};
